<script>
import { clearTokens, getAdminInfo } from '@/utils/token.js'

/** 사이드바 자동 축소 기준 너비 (px) */
const RAIL_BREAKPOINT = 1280

export default {
    name: 'AdminLayout',
    data() {
        return {
            windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1920,
            /**
             * 사이드바 네비게이션 메뉴 정의.
             * children 배열이 있는 항목은 v-list-group으로 렌더링한다.
             * children이 없는 항목은 단일 v-list-item으로 렌더링한다.
             */
            navItems: [
                { title: '대시보드', icon: 'mdi-view-dashboard-outline', to: '/dashboard' },
                { title: '현물시세', icon: 'mdi-chart-line', to: '/metal-price', hqOnly: true },
                { title: '영업일 관리', icon: 'mdi-calendar-clock-outline', to: '/business-calendar' },
                { title: '회원관리', icon: 'mdi-account-group-outline', to: '/members', hqOnly: true },
                {
                    title: '상품관리',
                    icon: 'mdi-package-variant',
                    hqOnly: true,
                    children: [
                        { title: '상품 카테고리 관리', icon: 'mdi-tag-outline', to: '/products/categories' },
                        { title: '상품 관리', icon: 'mdi-cube-outline', to: '/products' },
                    ],
                },
                { title: '매수·매도', icon: 'mdi-swap-horizontal', to: '/trade' },
                { title: '감정평가', icon: 'mdi-magnify-scan', to: '/appraisal' },
                { title: '패널티 목록', icon: 'mdi-alert-decagram-outline', to: '/appraisal-penalties' },
                { title: '인출하기', icon: 'mdi-package-variant-closed', to: '/order-status' },
                { title: '출금 신청', icon: 'mdi-bank-transfer-out', to: '/cash-withdraw', hqOnly: true },
                { title: '정산관리', icon: 'mdi-cash-register', to: '/settlement' },
                {
                    title: '고객지원',
                    icon: 'mdi-lifebuoy',
                    children: [
                        { title: '1:1 문의', icon: 'mdi-forum-outline', to: '/inquiries' },
                        { title: '공지사항', icon: 'mdi-bullhorn-outline', to: '/notices', hqOnly: true },
                        // { title: '자주묻는 질문', icon: 'mdi-help-circle-outline', to: '/faqs', hqOnly: true },
                    ],
                },
                
                { title: '지점관리', icon: 'mdi-store-outline', to: '/branches' },
                { title: '본사관리자 관리', icon: 'mdi-account-tie-outline', to: '/hq-admins', hqOnly: true },
                {
                    title: '감정평가 현물관리',
                    icon: 'mdi-tune-variant',
                    to: '/appraisal-spot-items',
                    hqOnly: true,
                },
                { title: '정책설정', icon: 'mdi-cog-outline', to: '/trade-policy', hqOnly: true },
            
            ],
            adminInfo: { adminName: '', adminType: '' },
        }
    },
    computed: {
        rail() {
            return this.windowWidth < RAIL_BREAKPOINT
        },
        /**
         * HQ 전용 메뉴(hqOnly: true)는 본사 관리자일 때만 표시한다.
         */
        filteredNavItems() {
            const isHq = this.adminInfo.adminType === 'HQ'
            return this.navItems
                .filter(item => !(item.hqOnly && !isHq))
                .map(item => {
                    if (!item.children?.length) return item
                    const children = item.children.filter(ch => !(ch.hqOnly && !isHq))
                    return { ...item, children }
                })
                .filter(item => !item.children || item.children.length > 0)
        },
        adminInitial() {
            return this.adminInfo.adminName ? this.adminInfo.adminName.charAt(0) : '관'
        },
        adminLabel() {
            const typeMap = { HQ: '본사', DIRECT: '직영점', AGENCY: '대리점', FRANCHISE: '가맹점' }
            return typeMap[this.adminInfo.adminType] || ''
        },
        /**
         * 현재 라우트가 상품관리 하위 경로인지 확인.
         * v-list-group의 기본 펼침 상태를 결정하는 데 사용한다.
         */
        isProductGroupActive() {
            const path = this.$route?.path || ''
            return path.startsWith('/products')
        },
        isCustomerSupportGroupActive() {
            const path = this.$route?.path || ''
            return path.startsWith('/inquiries') || path.startsWith('/notices') || path.startsWith('/faqs')
        },
    },
    mounted() {
        this.adminInfo = getAdminInfo()
        window.addEventListener('resize', this.onResize)
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.onResize)
    },
    methods: {
        onResize() {
            this.windowWidth = window.innerWidth
        },
        logout() {
            clearTokens()
            this.$router.push('/login')
        },
        /**
         * 해당 메뉴 항목이 그룹 메뉴인지 확인 (children 배열 유무).
         * @param {Object} item - navItems 항목
         */
        isGroup(item) {
            return Array.isArray(item.children) && item.children.length > 0
        },
        isGroupDrawerActive(item) {
            if (item.title === '상품관리') return this.isProductGroupActive
            if (item.title === '고객지원') return this.isCustomerSupportGroupActive
            return false
        },
    },
}
</script>

<template>
    <v-navigation-drawer
        class="admin-sidebar-surface"
        permanent
        :rail="rail"
        color="white"
        elevation="2"
        width="256"
    >
        <!-- 로고 -->
        <v-list-item class="py-4 px-4">
            <template #prepend>
                <v-icon color="primary" size="28">mdi-gold</v-icon>
            </template>
            <v-list-item-title class="text-h6 font-weight-bold text-primary">
                Aura Admin
            </v-list-item-title>
        </v-list-item>
        
        <v-divider />
        
        <!-- 네비게이션 메뉴 -->
        <v-list density="compact" nav class="mt-2">
            <template v-for="item in filteredNavItems" :key="item.title">
                
                <!-- 그룹 메뉴 (children 있음): v-list-group으로 depth 표현 -->
                <v-list-group
                    v-if="isGroup(item)"
                    :value="item.title"
                >
                    <template #activator="{ props: groupProps }">
                        <v-tooltip
                            :text="item.title"
                            location="right"
                            :disabled="!rail"
                        >
                            <template #activator="{ props: tooltipProps }">
                                <v-list-item
                                    v-bind="{ ...groupProps, ...tooltipProps }"
                                    :prepend-icon="item.icon"
                                    :title="item.title"
                                    active-color="primary"
                                    rounded="lg"
                                    class="mb-1"
                                    :active="isGroupDrawerActive(item)"
                                />
                            </template>
                        </v-tooltip>
                    </template>
                    
                    <!-- 하위 메뉴 항목들 -->
                    <v-tooltip
                        v-for="child in item.children"
                        :key="child.to"
                        :text="child.title"
                        location="right"
                        :disabled="!rail"
                    >
                        <template #activator="{ props: tooltipProps }">
                            <v-list-item
                                v-bind="tooltipProps"
                                :prepend-icon="child.icon"
                                :title="child.title"
                                :to="child.to"
                                active-color="primary"
                                rounded="lg"
                                class="mb-1"
                            />
                        </template>
                    </v-tooltip>
                </v-list-group>
                
                <!-- 단일 메뉴 항목 (children 없음) -->
                <v-tooltip
                    v-else
                    :text="item.title"
                    location="right"
                    :disabled="!rail"
                >
                    <template #activator="{ props: tooltipProps }">
                        <v-list-item
                            v-bind="tooltipProps"
                            :prepend-icon="item.icon"
                            :title="item.title"
                            :to="item.to"
                            active-color="primary"
                            rounded="lg"
                            class="mb-1"
                        />
                    </template>
                </v-tooltip>
            
            </template>
        </v-list>
        
        <!-- 하단: 로그아웃 -->
        <template #append>
            <v-divider />
            <v-list density="compact" nav class="my-1">
                <v-tooltip text="로그아웃" location="right" :disabled="!rail">
                    <template #activator="{ props: tooltipProps }">
                        <v-list-item
                            v-bind="tooltipProps"
                            prepend-icon="mdi-logout"
                            title="로그아웃"
                            rounded="lg"
                            @click="logout"
                        />
                    </template>
                </v-tooltip>
            </v-list>
        </template>
    </v-navigation-drawer>
    
    <v-app-bar class="admin-topbar-surface" elevation="1" color="white" height="60">
        <v-app-bar-title>
      <span class="text-subtitle-1 font-weight-medium text-grey-darken-2">
        {{ $route.meta?.title || 'Aura Admin' }}
      </span>
        </v-app-bar-title>
        <template #append>
<!--            <v-btn icon variant="text">-->
<!--                <v-icon>mdi-bell-outline</v-icon>-->
<!--            </v-btn>-->
            <v-chip
                v-if="adminLabel"
                size="small"
                color="primary"
                variant="tonal"
                label
                class="mr-2"
            >
                {{ adminLabel }}
            </v-chip>
            <v-avatar color="primary" size="32" class="mr-3" style="cursor:pointer;">
                <span class="text-caption text-white font-weight-bold">{{ adminInitial }}</span>
            </v-avatar>
        </template>
    </v-app-bar>
    
    <v-main class="admin-main-surface">
        <v-container fluid class="pa-6 admin-main-container">
            <slot />
        </v-container>
    </v-main>
</template>

<style scoped>
.v-navigation-drawer .v-list-item--active {
    background-color: rgba(205, 178, 122, 0.12);
}

.admin-main-container {
    max-width: 1400px;
}
</style>
